import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorView, NodeView } from '@atlaskit/editor-prosemirror/view';

import {
	type AgentManagedExtensionPluginState,
	pluginKey,
} from '../../common/agentManagedExtensionPluginType';
import { getAgentManagedExtensionNodeView } from '../nodeviews/agentManagedExtensionNodeView';

import { createAutoOpenConfigOnInsertView } from './auto-open-view';
import { createSelectedWhileConfigOpenView } from './selected-while-config-open-view';
import { applyAutoCloseOnSelectionChange, applyMeta, getMeta, initialState } from './state';

export interface CreatePluginArgs {
	portalProviderAPI: PortalProviderAPI;
	providerFactory?: ProviderFactory;
}

export const createPlugin = (
	args: CreatePluginArgs,
): SafePlugin<AgentManagedExtensionPluginState> =>
	new SafePlugin<AgentManagedExtensionPluginState>({
		key: pluginKey,
		props: {
			nodeViews: {
				bodiedExtension: getAgentManagedExtensionNodeView({
					portalProviderAPI: args.portalProviderAPI,
					providerFactory: args.providerFactory,
				}) as unknown as (
					node: PMNode,
					view: EditorView,
					getPos: () => number | undefined,
				) => NodeView,
			},
		},
		state: {
			init: initialState,
			apply(tr, prev, _oldState, newState) {
				const next = applyMeta(prev, getMeta(tr));
				return applyAutoCloseOnSelectionChange(next, tr, newState);
			},
		},
		view: (editorView) => {
			const autoOpen = createAutoOpenConfigOnInsertView(editorView);
			const selectedWhileConfigOpen = createSelectedWhileConfigOpenView(editorView);
			return {
				destroy: () => {},
				update: () => {
					autoOpen.update();
					selectedWhileConfigOpen.update();
				},
			};
		},
	});
